import PropTypes from 'prop-types'
import styled from 'styled-components/macro'

import { IconTruckDrayage, IconTruckDryvan } from '@loadsmart/icons'
import { getToken } from '@loadsmart/loadsmart-ui/dist/theming'

import { ButtonAction as Button } from 'legacy/components/Button/ButtonAction'
import { Checkbox } from 'legacy/components/Checkbox/Checkbox'
import { CarrierForm } from 'legacy/components/DialogSignup/CarrierForm/CarrierForm'
import { FormAlert } from 'legacy/components/FormAlert/FormAlert'
import { I18n } from 'legacy/components/I18n/i18n'
import { Input } from 'legacy/components/Input/Input'
import { screen } from 'legacy/styles/screen'

import { LaneBar } from './LaneBar'
import { ModeToggle } from './ModeToggle'
import { SignupDialogFormFooter } from './SignupDialogFormFooter'
import { SignupSuccess } from './SignupSuccess'

const Title = styled.header`
  width: 100%;
  margin: 0;
  margin-bottom: 24px;
  padding-bottom: 20px;

  color: ${getToken('color-neutral-darkest')};
  font-weight: bold;
  font-size: 22px;
  line-height: 24px;
  text-align: center;

  border-bottom: 1px solid ${getToken('color-neutral-light')};
`

const FormWrapper = styled.form`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
`

const InputWrapper = styled.div`
  position: relative;

  display: inline-flex;
  flex-basis: 100%;
  flex-direction: column;
  margin-bottom: 20px;

  ${screen.md} {
    flex-basis: ${({ fullWidth }) => (fullWidth ? '100%' : '49%')};
  }
`

const StyledAlert = styled(FormAlert)`
  width: 100%;
  margin-top: 2px;

  color: ${getToken('color-danger-dark')};

  background-color: ${getToken('color-danger-light')};
`

const WrapperCheckbox = styled.div`
  position: relative;

  margin-bottom: 20px;
  padding: 20px 20px 4px 20px;

  color: ${getToken('color-neutral-white')};

  background-color: ${getToken('color-neutral-darker')};
  border-radius: 5px;
`

const StyledInput = styled(Input)`
  font-size: 14px;
  line-height: 21px;

  border: ${({ error }) => `1px solid ${error ? getToken('color-danger-dark') : getToken('color-neutral-darker')}`};
`

const Label = styled.label`
  margin-bottom: 6px;

  font-size: 12px;
  line-height: 16px;
  text-transform: uppercase;
`

const CheckBoxLabel = styled.label`
  position: relative;

  display: block;
  margin-bottom: 16px;
  padding: 0 0 0 25px;

  color: ${getToken('color-neutral-white')};
  font-size: 12px;
  line-height: 18px;

  input {
    position: absolute;
    top: 3px;
    left: -8px;
  }

  a {
    color: ${getToken('color-neutral-white')};
    text-decoration: underline;
  }
`

const StyledCheckbox = styled(Checkbox)`
  &:before {
    top: 4px;

    width: 16px;
    height: 10px;

    border-color: ${getToken('color-neutral-white')};
    border-style: solid;
    border-width: 0 0 2px 2px;
  }
  &:after {
    top: 0;

    width: 24px;
    height: 24px;

    background-color: ${getToken('color-neutral-dark')};
    border: 2px solid;
    border-radius: 3px;
    box-shadow: none;
  }
`

const ServerErrorWrapper = styled.div`
  margin-bottom: 6px;
`

const StyledButton = styled(Button)`
  margin-bottom: 10px;

  :disabled {
    opacity: 0.7;
  }
`

const LinkButton = styled(Button)`
  color: ${getToken('color-primary')};
  font-weight: bold;
  text-decoration: underline;

  border: none;
`

const AlignCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`

function SignupDialog(props) {
  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    isLoadTaken,
    isSubmitting,
    lane,
    onClose,
    onResendEmail,
    onSuccessView,
    onToggleLogin,
    status,
    touched,
    values,
  } = props

  const title = I18n.get(
    `lb.outletDialog.signUp.title.${isLoadTaken ? 'loadTaken' : 'default'}${
      lane ? '.preferredLane' : ''
    }`
  )

  if (status?.submitted) {
    return (
      <SignupSuccess
        onView={onSuccessView}
        onResendEmail={onResendEmail}
        email={values.email}
        onClose={onClose}
      />
    )
  }

  return (
    <FormWrapper name="carrierSignupForm" onSubmit={handleSubmit}>
      <Title aria-label="Carrier sign up at Loadsmart">{title}</Title>
      {lane && (
        <LaneBar origin={lane.origin?.description} destination={lane.destination?.description} />
      )}
      <InputWrapper>
        <Label htmlFor="firstName">{I18n.get('lb.outletDialog.signUp.firstName')}</Label>
        <StyledInput
          name="firstName"
          type="text"
          aria-required="true"
          placeholder={I18n.get('lb.outletDialog.signUp.firstName')}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.firstName}
          error={errors.firstName && touched.firstName}
        />
        {errors.firstName && touched.firstName && <StyledAlert message={errors.firstName} />}
      </InputWrapper>
      <InputWrapper>
        <Label htmlFor="lastName">{I18n.get('lb.outletDialog.signUp.lastName')}</Label>
        <StyledInput
          name="lastName"
          type="text"
          aria-required="true"
          placeholder={I18n.get('lb.outletDialog.signUp.lastName')}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.lastName}
          error={errors.lastName && touched.lastName}
        />
        {errors.lastName && touched.lastName && <StyledAlert message={errors.lastName} />}
      </InputWrapper>
      <InputWrapper fullWidth>
        <Label htmlFor="email">{I18n.get('lb.outletDialog.signUp.email')}</Label>
        <StyledInput
          name="email"
          type="email"
          aria-required="true"
          placeholder={I18n.get('lb.outletDialog.signUp.email')}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.email}
          error={errors.email && touched.email}
        />
        {errors.email && touched.email && <StyledAlert message={errors.email} />}
      </InputWrapper>
      <InputWrapper>
        <Label htmlFor="password">{I18n.get('lb.outletDialog.signUp.password')}</Label>
        <StyledInput
          name="password"
          type="password"
          aria-required="true"
          placeholder={I18n.get('lb.outletDialog.signUp.password')}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.password}
          error={errors.password && touched.password}
        />
        {errors.password && touched.password && <StyledAlert message={errors.password} />}
      </InputWrapper>
      <InputWrapper>
        <Label htmlFor="repeatPassword">{I18n.get('lb.outletDialog.signUp.confirmPassword')}</Label>
        <StyledInput
          name="repeatPassword"
          type="password"
          aria-required="true"
          placeholder={I18n.get('lb.outletDialog.signUp.confirmPassword')}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.repeatPassword}
          error={errors.repeatPassword && touched.repeatPassword}
        />
        {errors.repeatPassword && touched.repeatPassword && (
          <StyledAlert message={errors.repeatPassword} />
        )}
      </InputWrapper>
      <InputWrapper>
        <Label htmlFor="dot_number">{I18n.get('lb.outletDialog.signUp.dotNumber')}</Label>
        <StyledInput
          name="dot_number"
          id="dot_number"
          type="number"
          aria-required="true"
          placeholder={I18n.get('lb.outletDialog.signUp.dotNumber')}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.dot_number}
          error={errors.dot_number && touched.dot_number}
        />
        {errors.dot_number && touched.dot_number && <StyledAlert message={errors.dot_number} />}
      </InputWrapper>
      <InputWrapper>
        <Label htmlFor="mc_number">{I18n.get('lb.outletDialog.signUp.mcNumber')}</Label>
        <StyledInput
          name="mc_number"
          id="mc_number"
          type="number"
          aria-required="true"
          placeholder={I18n.get('lb.outletDialog.signUp.mcNumber')}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.mc_number}
          error={errors.mc_number && touched.mc_number}
        />
        {errors.mc_number && touched.mc_number && <StyledAlert message={errors.mc_number} />}
      </InputWrapper>
      <InputWrapper fullWidth>
        <ModeToggle
          options={[
            {
              value: 'ftl',
              label: I18n.get('lb.outletDialog.signUp.modeToggle.ftl'),
              icon: () => {
                return <IconTruckDryvan />
              },
            },
            {
              value: 'drayage',
              label: I18n.get('lb.outletDialog.signUp.modeToggle.drayage'),
              icon: () => {
                return <IconTruckDrayage />
              },
            },
          ]}
          name="equipmentType"
          value={values.equipmentType ? values.equipmentType : 'ftl'}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.equipmentType}
          error={errors.equipmentType && touched.equipmentType}
        />
        {errors.equipmentType && touched.equipmentType && (
          <StyledAlert message={errors.equipmentType} />
        )}
      </InputWrapper>
      {values.equipmentType === 'drayage' && (
        <>
          <InputWrapper fullWidth>
            <Label htmlFor="scac">{I18n.get('lb.outletDialog.signUp.scacCode')}</Label>
            <StyledInput
              name="scac"
              placeholder={I18n.get('lb.outletDialog.signUp.scacCode')}
              aria-required="true"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.scac}
              error={errors.scac && touched.scac}
            />
            {errors.scac && touched.scac && <StyledAlert message={errors.scac} />}
          </InputWrapper>
          <WrapperCheckbox>
            <CheckBoxLabel>
              <StyledCheckbox
                type="checkbox"
                name="uiia"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.uiia}
                error={errors.uiia && touched.uiia}
              />
              {I18n.get('lb.outletDialog.validUIIA.part1')}
              <a
                href="https://www.uiia.org/motor-carriers-0"
                target="_blank"
                rel="noopener noreferrer"
              >
                {I18n.get('lb.outletDialog.validUIIA.link')}
              </a>
              {I18n.get('lb.outletDialog.validUIIA.part2')}
              {errors.uiia && touched.uiia && <StyledAlert message={errors.uiia} />}
            </CheckBoxLabel>
            <CheckBoxLabel>
              <StyledCheckbox
                type="checkbox"
                name="emodal"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.emodal}
                error={errors.emodal && touched.emodal}
              />
              {I18n.get('lb.outletDialog.drayageRequirements')}
              {errors.emodal && touched.emodal && <StyledAlert message={errors.emodal} />}
            </CheckBoxLabel>
          </WrapperCheckbox>
        </>
      )}

      <SignupDialogFormFooter />

      {errors.server && (
        <ServerErrorWrapper>
          <StyledAlert message={errors.server} />
        </ServerErrorWrapper>
      )}
      <StyledButton
        width="100%"
        type="submit"
        disabled={isSubmitting}
        activated
        square
        upperCase
        size="sm"
      >
        {I18n.get('lb.outletDialog.signUp.submit')}
      </StyledButton>
      <StyledButton
        width="100%"
        type="button"
        disabled={isSubmitting}
        onClick={onClose}
        square
        upperCase
        size="sm"
      >
        {I18n.get('lb.outletDialog.signUp.cancel')}
      </StyledButton>
      <AlignCenter>
        <LinkButton onClick={onToggleLogin}>{I18n.get('lb.outletDialog.goToSignup')}</LinkButton>
      </AlignCenter>
    </FormWrapper>
  )
}

SignupDialog.displayName = 'SignupDialogForm'

SignupDialog.propTypes = {
  values: PropTypes.shape({}),
  errors: PropTypes.shape({}),
  handleBlur: PropTypes.func,
  handleChange: PropTypes.func,
  handleSubmit: PropTypes.func,
  isLoadTaken: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  lane: PropTypes.shape({
    origin: PropTypes.shape({
      description: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    }),
    destination: PropTypes.shape({
      description: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    }),
  }),
  onClose: PropTypes.func,
  onResendEmail: PropTypes.func,
  onSuccessView: PropTypes.func,
  onToggleLogin: PropTypes.func.isRequired,
  status: PropTypes.shape({
    submitted: PropTypes.bool,
  }),
  touched: PropTypes.shape({}),
}

SignupDialog.defaultProps = {
  values: {},
  errors: {},
  handleBlur: undefined,
  handleChange: undefined,
  handleSubmit: undefined,
  isLoadTaken: false,
  isSubmitting: false,
  lane: undefined,
  onClose: () => {},
  onResendEmail: () => {},
  onSuccessView: () => {},
  status: {
    submitted: false,
  },
  touched: {},
}

const SignupDialogForm = CarrierForm(SignupDialog)

export { SignupDialogForm }
